// @flow
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  CssBaseline, 
  Dialog, 
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Build as DraftIcon,
  HourglassEmpty as ValidatingIcon,
  PhotoCamera as CameraIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  WhatsApp as SupportIcon,
  Work as ProcessingIcon
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import routes from '../routes';
import { AuthContext, setAuthToken } from '../context/auth';
import SignProcess from '../model/SignProcess';
import { toSentence } from '../util/string';
import { whatsappURL } from '../util/vendor';
import ApiResponse from '../model/ApiResponse';
import Webcam from "react-webcam";

const baseUrl = process.env.PUBLIC_URL || '';
const imageExt = 'png';
const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  appBarContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  logo: {
    height: 28,
    marginRight: theme.spacing(1),
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  poweredBy: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  validating: {
    color: theme.palette.grey[ '700' ],
  },
  processing: {
    color: theme.palette.grey[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error[ '600' ],
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  bottomButton: {
    marginTop: theme.spacing(2),
  },
  verificationImage: {
    display: 'block',
    margin: '0 auto',
    width: '99.5%',
    height: '99.5%',
  },
  cameraCard: {
    maxWidth: 450,
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  videoContainer: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  video: {
    transform: 'scaleX(-1)', // Flip video horizontally for mirror effect
  },
  cameraOverlay: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1,
  },
  canvasContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  actionButton: {
    margin: theme.spacing(0.5),
    fontWeight: 'bold',
    minWidth: 120,
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  processInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
  },
  supportList: {
    paddingTop: 0,
    marginTop: theme.spacing(1),
  },
  compactCardContent: {
    paddingBottom: theme.spacing(0.5),
    '&:last-child': {
      paddingBottom: theme.spacing(0.5),
    },
  },
  compactCardActions: {
    justifyContent: 'center',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1.5),
  },
}));

type Props = {
  isRetailClient: boolean,
  match?: any,
};

const VerificationTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    maxWidth: 400,
    maxHeight: 400,
    width: '40%',
    height: '40%',
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const Selfie = (props: Props) => {
  const { user, doGet } = useContext(AuthContext);
  const { t } = useTranslation();
  const { match, isRetailClient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();

  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();
  const [process, setProcess] = useState<?SignProcess>();
  const [photoBlob, setPhotoBlob] = useState<?Blob>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<?string>();
  const photoRef = useRef(null);

  const webcamRef = React.useRef(null);


  const capture = React.useCallback(
    () => {
     
      const imageSrc = webcamRef.current.getScreenshot();
      
      if (imageSrc) {
        // Get webcam video dimensions
        const video = webcamRef.current.video;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
                
        // Convert base64 to blob
        fetch(imageSrc)
          .then(res => res.blob())
          .then(blob => {
            setPhotoBlob(blob);
            
            // Draw to canvas with exact webcam dimensions
            let photo = photoRef.current;
            photo.width = videoWidth;
            photo.height = videoHeight;
            let ctx = photo.getContext('2d');
            
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, 0, 0, videoWidth, videoHeight);
            };
            img.src = imageSrc;
          })
          .catch(err => {
            console.error('Error converting image:', err);
            enqueueSnackbar(t('Error capturing image'), {
              variant: 'error'
            });
          });
      }
    },
    [webcamRef, enqueueSnackbar, t]
  );

  const loadData = () => {
    if (!process
      && match && match.params
      && match.params.code && match.params.token) {
      let path = 'retail';
      setAuthToken(null);
      if (!isRetailClient) {
        setAuthToken(match.params.token);
        path = 'token';
      }
      doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`)
        .then((response: ApiResponse) => {
          if (response.ok) {
            setProcess(SignProcess.parse(response.data));
          } else {
            setError(response.message || 'Error');
          }
        });
      if (!supportPhone) {
        doGet('/customer/support-phone')
          .then((response: ApiResponse) => setSupportPhone(response.data));
      }
      if (!agentPhone) {
        doGet('/customer/agent-phone')
          .then((response: ApiResponse) => setAgentPhone(response.data));
      }
    }
  };

  useEffect(loadData, []);

  // const getVideo = () => {
  //   navigator.mediaDevices
  //     .getUserMedia({
  //       video: true
  //     })
  //     .then((stream) => {
  //       const video = videoRef.current;
  //       video.srcObject = stream;
  //       video.play();
  //     })
  //     .catch((err) => {
  //       console.error(err);
  //       setError('Could not detect camera');
  //     });
  // };

  // useEffect(getVideo, [videoRef]);

  const sendPhoto = (): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise<boolean>(resolve => {
        process.saveVideo(new File([photoBlob], `${user.code}_selfie.${imageExt}`), true)
          .then((response: ApiResponse) => {
            if (response.ok) {
              setProcess(SignProcess.parse(response.data));
              enqueueSnackbar(t('Verification saved'), {
                variant: 'success',
              });
              enqueueSnackbar(t('Your process is VIDEO_LOADED'), {
                variant: 'success',
              });
              
              // Redirect to process page after successful upload
              history.push(`/process/${process?.code || ''}`);

              
            } else {
              enqueueSnackbar(t(response.message), {
                variant: 'error',
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise<boolean>(resolve => resolve(false));
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home}/>;
  }

  const clearPhoto = () => {
    let photo = photoRef.current;
    let ctx = photo.getContext('2d');
    ctx.clearRect(0, 0, photo.width, photo.height);
    setPhotoBlob(null);
  };

  return (
    <Container component="main" maxWidth="lg">
      <CssBaseline/>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <Box className={classes.appBarContent}>
            <div className={classes.brandContainer}>
              <img 
                src={`${baseUrl}/images/logo.png`} 
                alt="Logo" 
                className={classes.logo}
              />
              <div>
                <Typography variant="subtitle1" className={classes.brandText}>
                  {t('Selfie Verification')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('EcuaFirma Digital Signature Process')}
                </Typography>
              </div>
            </div>
            <Typography variant="caption" className={classes.poweredBy}>
              {t('Powered by eQon')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      
      <div className={classes.paper}>
        {(!process && !error) && [1, 2, 3].map((index) => (
          <Skeleton key={index} height={60} style={{ marginBottom: 8 }}/>
        ))}
        {!!error && (
          <Typography variant="subtitle2" className={classes.error}>
            {t(error)}
          </Typography>
        )}
        {!!process && (
          <>
            <Box className={classes.processInfo}>
              <Typography variant="h6" gutterBottom>
                {process.isDraft()
                && <DraftIcon className={classes.draft}/>}
                {(process.hasVideoLoaded() && ! process.isProcessing() && !process.isCompleted())
                && <ValidatingIcon className={classes.draft}/>}
                {(process.isProcessing() || process.isCompleted())
                && <ProcessingIcon className={classes.processing}/>}
                &nbsp;{t(toSentence(process.processType))}
              </Typography>
              <Typography variant="caption" className={classes.grey} display="block">
                {t(`Your process is ${process.state}`)} • {t('Duration')}: {process.durationValue()} {t(process.durationLabel()).toLowerCase()} • {t('Price')}: ${process.price.toFixed(2)}
              </Typography>
            </Box>

            {!photoBlob && (
              <Card variant="outlined" className={classes.cameraCard}>
                <CardHeader 
                  title={t('Take your selfie')}
                  subheader={t('Position your face in the center of the camera')}
                  titleTypographyProps={{ variant: 'h6' }}
                  subheaderTypographyProps={{ variant: 'body2' }}
                />
                <CardContent className={classes.compactCardContent}>
                  <Box className={classes.videoContainer}>
                    <Webcam audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                    />
                    <Box className={classes.cameraOverlay}>
                      <Button 
                        disabled={processing} 
                        variant="contained" 
                        size="large"
                        className={`${classes.actionButton} ${classes.primaryButton}`}
                        startIcon={<CameraIcon />}
                        onClick={capture}
                      >
                        {t('Take Picture')}
                      </Button>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" align="center" style={{ marginBottom: 6 }}>
                    {t('Image intro')}
                  </Typography>
                  <Box display="flex" justifyContent="center" mb={1}>
                    <VerificationTooltip
                      placement="top"
                      title={
                        <React.Fragment>
                          <img src={`${baseUrl}/verification-sample.jpg`}
                               alt=""
                               className={classes.verificationImage}/>
                        </React.Fragment>
                      }
                    >
                      <Button variant="outlined" size="small">
                        {t('Check example')}
                      </Button>
                    </VerificationTooltip>
                  </Box>
                </CardContent>
                <CardActions className={classes.compactCardActions}>
                </CardActions>
              </Card>
            )}

            {!!photoBlob && (
              <Card variant="outlined" className={classes.cameraCard}>
                <CardHeader 
                  title={t('Review your selfie')}
                  subheader={t('Verify the image quality before sending')}
                  titleTypographyProps={{ variant: 'h6' }}
                  subheaderTypographyProps={{ variant: 'body2' }}
                />
                <CardContent className={classes.compactCardContent}>
                  <Box className={classes.canvasContainer}>
                    <canvas 
                      ref={photoRef} 
                      style={{ 
                        borderRadius: 8,
                        maxWidth: '100%',
                        height: 'auto'
                      }}
                    />
                  </Box>
                </CardContent>
                <CardActions className={classes.compactCardActions}>
                  <Button 
                    disabled={processing} 
                    variant="outlined" 
                    className={classes.actionButton}
                    startIcon={<ClearIcon />}
                    onClick={clearPhoto}
                  >
                    {t('Clear Image')}
                  </Button>
                  <Button 
                    disabled={processing} 
                    variant="contained" 
                    className={`${classes.actionButton} ${classes.secondaryButton}`}
                    startIcon={<SendIcon />}
                    onClick={sendPhoto}
                  >
                    {t('Send Image')}
                  </Button>
                </CardActions>
              </Card>
            )}
          </>
        )}
      </div>
      
      {(agentPhone && user) && <List className={classes.supportList}>
        <ListItem
          button
          key="agentPhone"
          dense
          onClick={() => {
            const url = whatsappURL(agentPhone, t('Agent support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Agent phone')}/>
        </ListItem>
      </List>}
      {(supportPhone && user) && <List className={classes.supportList}>
        <ListItem
          button
          key="supportPhone"
          dense
          onClick={() => {
            const url = whatsappURL(supportPhone, t('Support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Support phone')}/>
        </ListItem>
      </List>}
      <Dialog
        disableEscapeKeyDown
        open={processing}
      >
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Selfie;
